import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { FileDown, Upload, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { bn } from "../lib/format";

export default function ImportProducts({ onDone }: { onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [update, setUpdate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);
  const inp = useRef<HTMLInputElement>(null);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/products/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "product_import_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const doImport = async () => {
    if (!file) {
      toast.error("আগে একটি ফাইল নির্বাচন করুন");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/products/import?update=${update}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      if (r.data.created > 0 || r.data.updated > 0) {
        toast.success(`${bn(r.data.created)} টি যোগ, ${bn(r.data.updated || 0)} টি আপডেট`);
        onDone();
      } else {
        toast.message("কোনো নতুন পণ্য যোগ/আপডেট হয়নি");
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="import-products">
      <p className="text-sm text-slate-600">এক্সেল (.xlsx) বা CSV ফাইল দিয়ে একসাথে অনেক পণ্য যোগ করুন। প্রথমে টেমপ্লেট ডাউনলোড করে সেই ফরম্যাটে পণ্যের তালিকা লিখুন, তারপর আপলোড করুন।</p>

      <button type="button" onClick={downloadTemplate} className="btn btn-ghost w-full" data-testid="import-template-button">
        <FileDown size={17} /> টেমপ্লেট ডাউনলোড (.xlsx)
      </button>

      <label className="block">
        <span className="block text-sm font-medium text-slate-600 mb-1">ফাইল নির্বাচন করুন</span>
        <input
          ref={inp}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
          className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 file:font-semibold hover:file:bg-blue-100 file:cursor-pointer"
          data-testid="import-file-input"
        />
      </label>
      {file && <p className="text-xs text-slate-500">নির্বাচিত ফাইল: <b>{file.name}</b></p>}

      <label className="flex items-start gap-2.5 text-sm text-slate-700 bg-blue-50/60 border border-blue-100 rounded-lg px-3 py-2.5 cursor-pointer">
        <input type="checkbox" className="w-4 h-4 mt-0.5 accent-blue-700" checked={update} onChange={(e) => setUpdate(e.target.checked)} data-testid="import-update-toggle" />
        <span>একই বারকোডের পণ্য আগে থেকে থাকলে <b>বাদ না দিয়ে স্টক/দর আপডেট</b> করুন।</span>
      </label>

      <button type="button" onClick={doImport} disabled={busy || !file} className="btn btn-primary w-full" data-testid="import-submit-button">
        {busy ? <><Loader2 size={17} className="animate-spin" /> ইমপোর্ট হচ্ছে...</> : <><Upload size={17} /> ইমপোর্ট করুন</>}
      </button>

      {result && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-2" data-testid="import-result">
          <p className="flex items-center gap-2 text-emerald-700 font-semibold">
            <CheckCircle2 size={16} /> {bn(result.created)} টি যোগ{result.updated ? `, ${bn(result.updated)} টি আপডেট` : ""}{result.skipped ? `, ${bn(result.skipped)} টি বাদ` : ""}
          </p>
          {result.errors?.length > 0 && (
            <ul className="text-xs text-red-600 space-y-1 max-h-40 overflow-y-auto">
              {result.errors.map((er: string, i: number) => (
                <li key={i} className="flex gap-1.5"><AlertTriangle size={13} className="shrink-0 mt-0.5" /> {er}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
